import { Injectable, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, BehaviorSubject, Subject } from 'rxjs';

import { AuthService } from 'gast-portal-ng';
import { HttpService } from 'gast-common-ng';
import { GastRequestModel } from 'gast-common-ng/lib/shared/models/gast-request.model';

import { RouteViewKeys } from '../constants/route-view-keys';
import { UrlService } from './common/url.service';
import { WebshopConfigViewDto } from '../dto/webshop-config-view-dto';
import { QueryParamsHandling } from '@angular/router/src/config';
import { WebshopConfigDto } from '../dto/webshop-config-dto';
import { WebshopDatafileDto } from '../dto/webshop-datafile-dto';

@Injectable()
export class BookingStepService {
    private steps = new BehaviorSubject<Array<string>>([]);
    private viewStep = new BehaviorSubject<Array<WebshopConfigViewDto>>([]);
    private config = new Subject<WebshopConfigDto>();
    private needValidation = false;
    @Output() viewStepLoaded = new EventEmitter();
    @Output() viewStepWithCodeLoaded = new EventEmitter();
    @Output() configLoaded = new EventEmitter();
    @Output() datafileLoaded = new EventEmitter<Array<WebshopDatafileDto>>();

    constructor(
        private router: Router,
        private authService: AuthService,
        private httpService: HttpService,
        private urlService: UrlService) { }

    /** get steps as observable */
    public getSteps(): Observable<any> {
        return this.steps.asObservable();
    }

    /** get steps as observable */
    public getViewSteps(): Observable<any> {
        return this.viewStep.asObservable();
    }

    /** get steps as observable */
    public getConfig(): Observable<any> {
        return this.config.asObservable();
    }

    /** get the index of the step */
    public getCurrentStepIndex(currentView: string, viewSteps: Array<WebshopConfigViewDto>, steps: Array<string>): number {
        const viewStep = viewSteps.find(x => x.viewName === currentView);
        if (viewStep) {
            return steps.indexOf(viewStep.stepName);
        } else {
            return 0;
        }
    }

    public getStartView(): string {
        const stepArray = this.viewStep.getValue();
        if (stepArray.length > 0) {
            const start = stepArray[0] as WebshopConfigViewDto;
            return start.viewName;
        }
    }

    /**
     * Go to start
     * 1. startView is empty string navigate to it, it will be redirected to course
     * 2. startView is login check if user logged in redirect to to next view after login
     * 3. step is not initialize redirect to course
     * NOTE: queryParamsHandling per default is merge
     */
    public goToStart(queryParamHandlingOptional?: string): void {
        let queryParamHandlingOption = 'merge';
        if (queryParamHandlingOptional) {
            queryParamHandlingOption = queryParamHandlingOptional;
        }
        const queryParamOption = queryParamHandlingOption as QueryParamsHandling;

        const stepArray = this.viewStep.getValue();
        if (stepArray.length > 0) {
            const start = stepArray[0] as WebshopConfigViewDto;
            if (start.viewName === '') {
                this.router.navigate([start.viewName], { queryParamsHandling: queryParamOption });
            } else if (start.viewName === RouteViewKeys.LOGIN && this.isUserLoggedIn()) {
                const nextAfterLogin = stepArray[1] as WebshopConfigViewDto;
                this.router.navigate(['course/' + nextAfterLogin.viewName], { queryParamsHandling: queryParamOption });
            } else {
                this.router.navigate(['course/' + start.viewName], { queryParamsHandling: queryParamOption });
            }
        } else {
            this.router.navigate(['course'], { queryParamsHandling: queryParamOption });
        }
    }

    public goToShoppingCart(): void {
        this.router.navigate(['course/' + RouteViewKeys.SHOPPING_CART], { queryParamsHandling: 'merge' });
    }

    public goToSummary(): void {
        this.router.navigate(['course/' + RouteViewKeys.SUMMARY], { queryParamsHandling: 'merge' });
    }

    public goToCourseClassification(): void {
        this.router.navigate(['course/' + RouteViewKeys.CLASSIFICATION], { queryParamsHandling: 'merge' });
    }

    public goToBookingConfirmation(): void {
        this.router.navigate(['course/' + RouteViewKeys.CONFIRMATION], { queryParamsHandling: 'merge' });
    }

    public goToVerification(): void {
        this.router.navigate(['course/' + RouteViewKeys.VERIFICATION], { queryParamsHandling: 'merge' });
    }

    /** go to next step */
    public goToNextStep(currentView: string, queryParams?: any): void {
        const routeConfig = this.viewStep.getValue() as Array<WebshopConfigViewDto>;
        let currentIndex = routeConfig.findIndex(v => v.viewName === currentView);
        if (currentView === 'course-detail') {
          currentIndex = 0;
        }
        const nextView = routeConfig[currentIndex + 1].viewName;
        if (nextView === RouteViewKeys.LOGIN && this.isUserLoggedIn()) {
            const nextViewAfterLogin = routeConfig[currentIndex + 2].viewName;
            this.router.navigate(['course/' + nextViewAfterLogin], { queryParams: queryParams, queryParamsHandling: 'merge' });
        } else {
            this.router.navigate(['course/' + nextView], { queryParams: queryParams, queryParamsHandling: 'merge' });
        }
    }

    public loadDefaultSteps(): void {
        this.httpService.get({
            mapper: (response: Response) => <any>response.json(),
            callback: (result: Array<string>) => {
                this.steps.next(result);
                this.loadDefaultViewSteps();
            },
            url: this.urlService.getWebshopStepUrl()
        } as GastRequestModel<any>);
    }

    public loadWebshopConfig(code: string): void {
        this.httpService.get({
            mapper: (response: Response) => <any>response.json(),
            callback: (result: WebshopConfigDto) => {
                this.configLoaded.emit(result);
            },
            url: this.urlService.getWebshopConfigUrl() + `/${code}`
        } as GastRequestModel<any>);
    }

    public loadStepsByCode(code: string): void {
        this.httpService.get({
            mapper: (response: Response) => <any>response.json(),
            callback: (result: Array<string>) => {
                this.steps.next(result);
                this.loadViewStepsByCode(code);
            },
            url: this.urlService.getWebshopStepUrl() + `/${code}`
        } as GastRequestModel<any>);
    }

    public loadDefaultViewSteps(): void {
        this.httpService.get({
            mapper: (response: Response) => <any>response.json(),
            callback: (result: Array<WebshopConfigViewDto>) => {
                this.viewStep.next(result);
                this.viewStepLoaded.emit();
            },
            url: this.urlService.getWebshopViewStepUrl()
        } as GastRequestModel<any>);
    }

    public loadViewStepsByCode(code: string): void {
        this.httpService.get({
            mapper: (response: Response) => <any>response.json(),
            callback: (result: Array<WebshopConfigViewDto>) => {
                this.viewStep.next(result);
                this.viewStepWithCodeLoaded.emit();
            },
            url: this.urlService.getWebshopViewStepUrl() + `/${code}`
        } as GastRequestModel<any>);
    }

    public loadWebshopDatafileByWebshopConfig(webshopConfigId: number): void {
        this.httpService.get({
            mapper: (response: Response) => <any>response.json(),
            callback: (result: Array<WebshopDatafileDto>) => {
                this.datafileLoaded.emit(result);
            },
            url: this.urlService.getWebshopDatafileUrl() + `/${webshopConfigId}`
        } as GastRequestModel<any>);
    }

    /** check if current user is logged in */
    public isUserLoggedIn(): boolean {
        const userInfo = this.authService.readUserInfo();
        if (userInfo && userInfo.username) {
            return true;
        }
        return false;
    }

    public setNeedValidation () {
      this.needValidation = true;
    }

  public getNeedValidation (): boolean {
    return this.needValidation;
  }
}
